interface AddressProps {
  streetAddress: string
  addressLocality: string
  addressRegion: string
  postalCode: string
  addressCountry: string
}

interface ContactPointProps {
  contactType: string
  telephone: string
  email: string
  areaServed?: string
  availableLanguage?: string
}

interface FounderProps {
  name: string
  sameAs?: string
  hasOccupation?: {
    name: string
    description: string
    mainEntityOfPage: {
      '@type': string
      '@id': string
      lastReviewed: string
    }
  }
  alumniOf?: {
    '@type': string
    name: string
  }
}

interface OrganizationProps {
  organizationStructure?: {
    name: string
    url: string
    logo: string
    description: string
    address: AddressProps
    founder: Array<{ name: string }>
  }
  enableOrganization?: boolean
  comprehensiveOrganization?: {
    name: string
    url: string
    logo: {
      '@type': string
      url: string
    }
    sameAs: string[]
    foundingDate: string
    foundingLocation: {
      '@type': string
      name: string
    }
    address: AddressProps[]
    contactPoint: ContactPointProps[]
    founder: FounderProps[]
    areaServed: string
    knowsAbout: string[]
    publishingPrinciples: string
    brand: {
      '@type': string
      name: string
      url: string
    }
    memberOf: {
      '@type': string
      name: string
    }
    award: string[]
    aggregateRating: {
      '@type': string
      ratingValue: string
      reviewCount: string
      bestRating: string
      ratingExplanation: string
    }
    review: {
      '@type': string
      author: {
        '@type': string
        name: string
        url: string
      }
      reviewRating: {
        '@type': string
        ratingValue: string
        bestRating: string
      }
    }
  }
}

export const StructuredOrganization = ({
  organizationStructure,
  enableOrganization,
  comprehensiveOrganization,
}: OrganizationProps) => {
  const generateOrganizationSchema = () => {
    if (organizationStructure && enableOrganization) {
      const { name, url, logo, description, founder, address } =
        organizationStructure

      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name,
        url,
        logo,
        description,
        address: {
          '@type': 'PostalAddress',
          streetAddress: address.streetAddress,
          addressLocality: address.addressLocality,
          addressRegion: address.addressRegion,
          postalCode: address.postalCode,
          addressCountry: address.addressCountry,
        },
        founder: founder?.map((f) => ({
          '@type': 'Person',
          name: f.name,
        })),
      }
    }

    if (comprehensiveOrganization) {
      const org = comprehensiveOrganization

      return {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            name: org.name,
            url: org.url,
            logo: org.logo,
            sameAs: org.sameAs,
            foundingDate: org.foundingDate,
            foundingLocation: org.foundingLocation,
            address: org.address?.map((addr) => ({
              '@type': 'PostalAddress',
              streetAddress: addr.streetAddress,
              addressLocality: addr.addressLocality,
              addressRegion: addr.addressRegion,
              postalCode: addr.postalCode,
              addressCountry: addr.addressCountry,
            })),
            contactPoint: org.contactPoint?.map((contact) => ({
              '@type': 'ContactPoint',
              contactType: contact.contactType,
              telephone: contact.telephone,
              email: contact.email,
              ...(contact.areaServed && { areaServed: contact.areaServed }),
              ...(contact.availableLanguage && {
                availableLanguage: contact.availableLanguage,
              }),
            })),
            founder: org.founder?.map((founder) => ({
              '@type': 'Person',
              name: founder.name,
              ...(founder.sameAs && { sameAs: founder.sameAs }),
              ...(founder.hasOccupation && {
                hasOccupation: founder.hasOccupation,
              }),
              ...(founder.alumniOf && { alumniOf: founder.alumniOf }),
            })),
            areaServed: org.areaServed,
            knowsAbout: org.knowsAbout,
            publishingPrinciples: org.publishingPrinciples,
            brand: org.brand,
            memberOf: org.memberOf,
            award: org.award,
            aggregateRating: org.aggregateRating,
            review: org.review,
          },
        ],
      }
    }

    return null
  }

  const schema = generateOrganizationSchema()

  if (!schema) return null

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  )
}
